import { AfterViewInit, Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { EventData, MapComponent as NgxMapComponent, NgxMapLibreGLModule } from '@maplibre/ngx-maplibre-gl';
import { GeoJSONSource, GeoJSONSourceOptions, LngLat, Map, MapGeoJSONFeature, MapMouseEvent, Marker } from 'maplibre-gl';
import { MapService } from './map.service';
import { IPOI, IPOICategory, POIService } from './poi.service';
import { BehaviorSubject, Subject, forkJoin, last, map, tap } from 'rxjs';
import { FeatureCollection, Point } from 'geojson';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { ConjectFMService, IFloor, IRoom } from './conjectfm.service';
import { Geolocation } from '@capacitor/geolocation';
import { RoutingService } from './routing.service';
import { AlertController, IonicSafeString, IonRouterOutlet, LoadingController, ModalController, NavController, ToastController, IonicModule } from '@ionic/angular';
import { Clipboard } from '@capacitor/clipboard';
import { OverlayEventDetail } from '@ionic/core';
import { AppointmentRoom, StudySpacesService } from 'src/app/pages/home/study-areas/learning-spaces/study-spaces.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Location, AsyncPipe, KeyValuePipe } from '@angular/common';
import { Share } from '@capacitor/share';
import { RoomTimetablePage } from '../../pages/home/study-areas/learning-spaces/room-timetable/room-timetable.page';
import { RoomSelectionComponent } from 'src/app/pages/home/study-areas/learning-spaces/room-selection/room-selection.component';
import { CategoryControlComponent } from './controls/category/category.component';
import { GeolocationControlComponent } from './controls/geolocation/geolocation.component';
import { HomeControlComponent } from './controls/home/home.component';
import { SearchControlComponent } from './controls/search/search.component';
import { HumanizedDatePipe } from '../pipes/humanized-date.pipe';

type ClickHandler = (ev: MapMouseEvent & { features?: MapGeoJSONFeature[]; } & Object) => void;

export interface CustomMapGeoJSONFeature {
  id: number | string;
  coordinates: number[];
  name: { de: string, en: string};
  description: { de: string, en: string };
  roomDescriptions: { [key: number]: { de: string, en: string } }[];
  groupedRooms: Record<number, { floor: IFloor, rooms: IRoom[] }>;
  occupancyLevel: 'HIGH' | 'MEDIUM' | 'LOW';
  category: IPOICategory;
  hasStudySpaces: boolean;
  metaRooms: AppointmentRoom[];
}

@Component({
    selector: 'app-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
    imports: [NgxMapLibreGLModule, CategoryControlComponent, GeolocationControlComponent, HomeControlComponent, SearchControlComponent, IonicModule, AsyncPipe, KeyValuePipe, TranslatePipe, HumanizedDatePipe]
})
export class MapComponent implements OnInit, OnDestroy, AfterViewInit {
  private navController = inject(NavController);
  private mapService = inject(MapService);
  private poiService = inject(POIService);
  private translate = inject(TranslateService);
  private routingService = inject(RoutingService);
  private modalController = inject(ModalController);
  private alertController = inject(AlertController);
  private zone = inject(NgZone);
  private loadingController = inject(LoadingController);
  private studySpacesService = inject(StudySpacesService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  private routerOutlet = inject(IonRouterOutlet);
  private toastController = inject(ToastController);

  public INITIAL_MODAL_BREAKPOINT = 0.4;
  private FLY_TO_ZOOM: number = 18;

  @Input()
  public trackUserLocation = false;

  @Input()
  public enabledCategories: number[] = [];

  @Input()
  public showCategories = false;

  @Input()
  public showSearch = true;

  @Output()
  public changeCategories: EventEmitter<number[]> = new EventEmitter();

  public categories: IPOICategory[] = [];
  public selectedPOI: CustomMapGeoJSONFeature = null;
  public appointmentRooms: AppointmentRoom[] | null = null;
  public featureCollection: GeoJSON.FeatureCollection = null;
  public languageCode: string = 'de';
  public loadingCurrentPosition = false;

  @ViewChild(NgxMapComponent)
  private mapComponent: NgxMapComponent;

  private mapLoaded$ = new Subject<Map>();
  private sourceDataLoaded$ = new Subject<any>();
  private layerClickHandlers: { [key: string]: ClickHandler } = {};
  private mouseEnterHandlers: { [key: string]: () => void } = {};
  private mouseLeaveHandlers: { [key: string]: () => void } = {};
  public loaded = new BehaviorSubject<boolean>(false);
  private pois: IPOI[] = [];
  private startMarker: Marker = null;

  public ngAfterViewInit() {
    this.sourceDataLoaded$.pipe(last()).subscribe((features) => {
      this.loaded.next(true);
      this.loaded.complete();
      this.loadingController.dismiss();
      this.activePOIByPathParam(features);
    });

    forkJoin([
        this.poiService.categories(),
        this.mapService.detailedPOIs(),
    ]).pipe(
      tap(([categories, pois]) => {
        this.categories = categories;
        this.pois = pois;
      }),
      map(([categories, pois]) => this.mapService.geoJSONFeatureCollection(pois, categories))
    ).subscribe((featureCollection) => {
      this.featureCollection = featureCollection;
      this.addCategoryByPathParam();
      this.setupPOIs();
    });
  }

  public async ngOnInit() {
    this.languageCode = (this.translate.currentLang() ?? 'de-DE').split('-')[0] ?? 'de';

    const spinner = await this.loadingController.create({
      backdropDismiss: true
    });
    spinner.onDidDismiss().then(async (overlayDetail: OverlayEventDetail) => {
      if(overlayDetail.role === 'error') {
        this.navController.navigateBack('/home');
        const alert = await this.alertController.create({
          header: this.translate.instant('core.errors.serviceError.header'),
          message: this.translate.instant('core.errors.serviceError.message'),
          buttons: ['OK']
        });
        alert.present();
      }
    });
    spinner.present();
  }

  public async ngOnDestroy() {
    // The modal view will hardly ever be dismissed at this point. It'll most likely be dismissed in the encapsulating
    // Ionic Page (e.g. campus-navi or study-areas) since its a better user experience to dissmiss the modal on ionViewWillLeave.
    const mapDetailView = document.querySelector('map-detail-view');
    if(mapDetailView) {
      this.modalController.dismiss(undefined, undefined, 'map-detail-view');
    }
  }

  public async mapLoaded(map: Map) {
    this.mapLoaded$.next(map);
    if(map) {
      this.mapLoaded$.complete();
    }
  }
  
  public sourceDataLoaded(sourceDataEvent: { source: { data: FeatureCollection }} & any) {
    this.sourceDataLoaded$.next(sourceDataEvent?.source?.data);
    if(sourceDataEvent?.source?.data) {
      this.sourceDataLoaded$.complete();
    }
  }

  public mapResized(): void {
    if(this.selectedPOI) {
      const [lng, lat] = this.selectedPOI.coordinates;
      this.mapComponent.mapInstance.flyTo({
        center: new LngLat(lng, lat),
        zoom: this.FLY_TO_ZOOM,
        offset: [0, -this.offsetBasedOnModal()]
      });
    }
  }

  public mapError(_: ErrorEvent & EventData) {
    this.loadingController.dismiss(null, 'error');
  }

  private addCategoryByPathParam(): void {
    try {
      const categoryId: number = parseInt(this.activatedRoute.snapshot.params['category']);
      this.addCategories([categoryId]);
    } catch (error) {}
  }

  private addCategories(categoryIds: number[]): void {
    const validCategoryIds = new Set(this.categories.map((category: IPOICategory): number => category.id));
    const result = new Set(this.enabledCategories);

    for (const id of categoryIds) {
      if (validCategoryIds.has(id)) {
        result.add(id);
      }
    }

    this.updateFilter([...result]);
  }

  private async activePOIByPathParam(featureCollection: { features: MapGeoJSONFeature[] }): Promise<void> {
    const poiId: string = this.activatedRoute.snapshot.params['poi'];
    const categoryId: number = parseInt(this.activatedRoute.snapshot.params['category']);
    const poi = featureCollection.features.find(f => f.properties.id === poiId);
    if(!poi || categoryId === undefined || categoryId === null) return;
    const sortedRooms = poi.properties.rooms ? poi.properties.rooms.sort(POIService.sortRoomByFloorAndRoomNo) : null;
    const sortedGroupedRooms = sortedRooms ? ConjectFMService.groupRoomsByFloor(sortedRooms) : null;

    const customFeature: CustomMapGeoJSONFeature & any = {
      ...poi.properties,
      groupedRooms: sortedGroupedRooms
    };

    await this.openPOI(customFeature);
  }

  public updateFilter(categoryIds: number[]) {
    ((this.mapComponent.mapInstance.getSource('poi-source') as GeoJSONSource).workerOptions as GeoJSONSourceOptions).filter = ['in', ['get', 'id', ['get', 'category']], ['literal', categoryIds.length ? categoryIds : null]];
    (this.mapComponent.mapInstance.getSource('poi-source') as GeoJSONSource)._updateWorkerData();

    for(const category of this.categories) {
      if(categoryIds.includes(category.id)) {
        this.addCategoryLayer(category.id);
      } else {
        if(this.mapComponent.mapInstance.getLayer(`pois-${category.id}`)) {
          this.mapComponent.mapInstance.removeLayer(`pois-${category.id}`);
        }
        if(this.mapComponent.mapInstance.getLayer(`pois-symbol-${category.id}`)) {
          this.mapComponent.mapInstance.removeLayer(`pois-symbol-${category.id}`);
        }
      }
    }
    this.changeCategories.emit(categoryIds);
  }

  private setupPOIs(): void {
    this.mapComponent.mapInstance.addSource(`poi-source`, {
      type: 'geojson',
      data: this.featureCollection,
      cluster: true,
      clusterRadius: 50,
    });

    this.updateFilter(this.enabledCategories);

    this.mapComponent.mapInstance.addLayer({
      id: `pois-cluster`,
      type: 'circle',
      source: 'poi-source',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#639a00',
        'circle-radius': 18,
        'circle-stroke-color': '#000000',
        'circle-stroke-opacity': 0.5,
        'circle-stroke-width': 2
      }
    });

    this.mapComponent.mapInstance.addLayer({
      id: `pois-cluster-counter`,
      type: 'symbol',
      source: 'poi-source',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count}',
        'text-font': ['Noto Sans Bold']
      },
      paint: {
        'text-color': '#FFFFFF',
      }
    });

    // Zoom into map on cluster click
    this.mapComponent.mapInstance.on('click', `pois-cluster`, (clickEvent: any) => {
      this.zone.run(() => {
        this.selectedPOI = null;
      });

      const features = this.mapComponent.mapInstance.queryRenderedFeatures(clickEvent.point, {
          layers: [`pois-cluster`]
      });
      const clusterId = features[0].properties.cluster_id;
      (this.mapComponent.mapInstance.getSource('poi-source') as GeoJSONSource).getClusterExpansionZoom(
          clusterId,
          (err, zoom) => {
              if (err) return;
              this.mapComponent.mapInstance.easeTo({
                center: new LngLat((features[0].geometry as GeoJSON.Point).coordinates[0], (features[0].geometry as GeoJSON.Point).coordinates[1]),
                zoom
              });
          }
      );
    });

    for(const category of this.categories.filter(category => this.enabledCategories.includes(category.id))) {
      this.addCategoryLayer(category.id);
    }
  }

  private addCategoryLayer(categoryId: number): void {
    const poiLayer = this.mapComponent.mapInstance.getLayer(`pois-${categoryId}`);
    const poiSymbolLayer = this.mapComponent.mapInstance.getLayer(`pois-symbol-${categoryId}`);

    if(!poiLayer) {
      this.mapComponent.mapInstance.addLayer({
        id: `pois-${categoryId}`,
        type: 'circle',
        source: 'poi-source',
        filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'id', ['get', 'category']], categoryId]],
        paint: {
          'circle-color': '#639a00',
          'circle-radius': 20,
          'circle-stroke-width': [
            'case',
            ['==', ['has', 'occupancyLevel'], true],
            7.5,
            0
          ],
          'circle-stroke-opacity': [
            'case',
            ['==', ['has', 'occupancyLevel'], true],
            0.4,
            1
          ],
          'circle-stroke-color': [
            'case',
            ['==', ['get', 'occupancyLevel'], 'LOW'],
            '#639a00',
            ['==', ['get', 'occupancyLevel'], 'MEDIUM'],
            'orange',
            ['==', ['get', 'occupancyLevel'], 'HIGH'],
            'red',
            'black'
          ],
        }
      });
    }

    // Add category icon on POI
    if(!poiSymbolLayer) {
      this.mapComponent.mapInstance.addLayer({
        id: `pois-symbol-${categoryId}`,
        type: 'symbol',
        source: 'poi-source',
        filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'id', ['get', 'category']], categoryId]],
        layout: {
          'text-font': ['Font Awesome 6 Free Solid'],
          'text-field': ['get', 'icon'],
        },
        paint: {
          'text-color': '#FFFFFF'
        }
      });
    }

    let clickHandler = this.layerClickHandlers[categoryId];
    if(!clickHandler) {
      clickHandler = async (mapMouseEvent: MapMouseEvent & { features?: MapGeoJSONFeature[]; }) => {
        const customFeature: CustomMapGeoJSONFeature = MapComponent.convertFromMapGeoJSONFeatures(mapMouseEvent.features[0]);
        const segments = this.activatedRoute.snapshot.pathFromRoot.flatMap(route => route.url).map(segment => segment.path);
        const urlTree = this.router.createUrlTree(['/', segments[0], categoryId, customFeature.id])
        const url = this.router.serializeUrl(urlTree);
        this.location.replaceState(url);
        await this.openPOI(customFeature);
      };
      this.layerClickHandlers[categoryId] = clickHandler;
      this.mapComponent.mapInstance.on('click', `pois-${categoryId}`, clickHandler);
    }

    let mouseEnterHandler = this.mouseEnterHandlers[categoryId];
    if(!mouseEnterHandler) {
      mouseEnterHandler = () => {
        this.mapComponent.mapInstance.getCanvas().style.cursor = 'pointer';
      };
      this.mouseEnterHandlers[categoryId] = mouseEnterHandler;
      this.mapComponent.mapInstance.on('mouseenter', `pois-${categoryId}`, mouseEnterHandler);
    }

    let mouseLeaveHandler = this.mouseLeaveHandlers[categoryId];
    if(!mouseLeaveHandler) {
      mouseLeaveHandler = () => {
        this.mapComponent.mapInstance.getCanvas().style.cursor = '';
      };
      this.mouseLeaveHandlers[categoryId] = mouseLeaveHandler;
      this.mapComponent.mapInstance.on('mouseleave', `pois-${categoryId}`, mouseLeaveHandler);
    }
  }

  public async openPOI(customFeature: CustomMapGeoJSONFeature) {
    if(customFeature.category.id) {
      this.addCategories([customFeature.category.id]);
    }

    const modal = await this.modalController.getTop();
    if(modal) {
      await modal.setCurrentBreakpoint(this.INITIAL_MODAL_BREAKPOINT);
    }

    const [lng, lat] = customFeature.coordinates;

    this.mapComponent.mapInstance.flyTo({
      center: new LngLat(lng, lat),
      zoom: this.FLY_TO_ZOOM,
      offset: [0, -this.offsetBasedOnModal()]
    });

    this.zone.run(() => {
      this.selectedPOI = customFeature;
    });

    this.zone.run(() => {
      const buildingId = Number(customFeature?.id);
      if(customFeature?.id !== null && buildingId) {
        this.studySpacesService.getRoomsByBuilding(buildingId).subscribe((appointmentRooms: AppointmentRoom[]) => {
          this.appointmentRooms = appointmentRooms;
        });
      }
    })
  }

  private offsetBasedOnModal(): number {
    return (this.mapComponent.mapContainer.nativeElement.offsetHeight * this.INITIAL_MODAL_BREAKPOINT) / 2;
  }

  public static convertFromMapGeoJSONFeatures(features: MapGeoJSONFeature): CustomMapGeoJSONFeature {
    const id: string = features.properties.id;
    const coordinates = (features.geometry as Point).coordinates.slice()
    const name = JSON.parse(features.properties.name) ?? null;
    const description = features.properties.description ? JSON.parse(features.properties.description) : null;
    const roomDescriptions = features.properties.roomDescriptions ? JSON.parse(features.properties.roomDescriptions) : null;
    const rooms: IRoom[] = features.properties.rooms ? JSON.parse(features.properties.rooms) : null;
    const occupancyLevel: 'LOW' | 'MEDIUM' | 'HIGH' = features.properties.occupancyLevel;
    const category: IPOICategory = features.properties.category ? JSON.parse(features.properties.category) : null;
    const hasStudySpaces = features.properties.hasStudySpaces ? JSON.parse(features.properties.hasStudySpaces) : false;

    const sortedRooms = rooms ? rooms.sort(POIService.sortRoomByFloorAndRoomNo) : null;
    const sortedGroupedRooms = sortedRooms ? ConjectFMService.groupRoomsByFloor(sortedRooms) : null;

    return {
      id,
      coordinates,
      name,
      description,
      roomDescriptions,
      groupedRooms: sortedGroupedRooms,
      occupancyLevel,
      category,
      hasStudySpaces,
      metaRooms: []
    }
  }

  public async addRoute(coordinates: number[]) {
    this.loadingCurrentPosition = true;
    const currentPosition = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, maximumAge: 2000 });

    if(!currentPosition) {
      this.loadingCurrentPosition = false;
      return;
    }

    this.routingService.route(coordinates[1], coordinates[0], currentPosition.coords.latitude, currentPosition.coords.longitude).subscribe(async (path: number[][]) => {
      this.loadingCurrentPosition = false;
      const routingFeature = this.geoJSONFeatureFromPath(path);
      const startFeature = this.geoJSONFeatureFromPath([[currentPosition.coords.longitude, currentPosition.coords.latitude], path[path.length - 1]]);
      const endFeature = this.geoJSONFeatureFromPath([[coordinates[0], coordinates[1]], path[0]]);
      this.exitNavigation();

      this.mapComponent.mapInstance.addSource('route', {
        type: 'geojson',
        data: routingFeature
      });

      this.mapComponent.mapInstance.addSource('startRoute', {
        type: 'geojson',
        data: startFeature
      });

      this.mapComponent.mapInstance.addSource('endRoute', {
        type: 'geojson',
        data: endFeature
      });

      this.mapComponent.mapInstance.addLayer({
        id: 'route',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#eb445a',
          'line-width': 6,
        }
      });

      this.mapComponent.mapInstance.addLayer({
        id: 'startRoute',
        type: 'line',
        source: 'startRoute',
        layout: {
          'line-cap': 'round'
        },
        paint: {
          'line-width': 4,
          'line-dasharray': [2, 2],
        }
      });

      this.mapComponent.mapInstance.addLayer({
        id: 'endRoute',
        type: 'line',
        source: 'endRoute',
        layout: {
          'line-cap': 'round'
        },
        paint: {
          'line-width': 4,
          'line-dasharray': [2, 2]
        }
      });

      if(!this.startMarker) {
        this.startMarker = new Marker({ color: '#1da1f2' });
      }
      this.startMarker.remove();
      const currentPositionLngLat = new LngLat(currentPosition.coords.longitude, currentPosition.coords.latitude);
      this.startMarker.setLngLat(currentPositionLngLat).addTo(this.mapComponent.mapInstance);

      this.mapComponent.mapInstance.flyTo({
        center: currentPositionLngLat,
      });
      const modal = await this.modalController.getTop();
      if(modal) {
        modal.dismiss();
      }
    });
  }

  public async openTimetable(buildingId: number | string, roomId: number) {
    const modal = await this.modalController.create({
      component: RoomTimetablePage,
      presentingElement: this.routerOutlet.nativeEl,
      componentProps: {
        buildingId,
        roomId
      }
    });
    modal.present();
  }

  public async openRoomOverview(buildingId: number | string) {
    const modal = await this.modalController.create({
      component: RoomSelectionComponent,
      presentingElement: this.routerOutlet.nativeEl,
      componentProps: {
        buildingId
      }
    });
    modal.present();
  }

  public async copyCoordinates(poi: CustomMapGeoJSONFeature) {
    Clipboard.write({
      string: `${poi.coordinates[1]} ${poi.coordinates[0]}`
    });

    const toast = await this.toastController.create({
      message: new IonicSafeString(this.translate.instant('map.copyCoordinates.message', { name: poi.name[this.languageCode] })),
      position: 'bottom',
      duration: 1500
    });
    toast.present();
  }

  public isNavigating(): boolean {
    return !!this.mapComponent && !!this.mapComponent.mapInstance && !!this.mapComponent.mapInstance.getLayer("route");
  }

  public exitNavigation() {
    const startRoute = this.mapComponent.mapInstance.getLayer("startRoute");
    const endRoute = this.mapComponent.mapInstance.getLayer("endRoute");
    const route = this.mapComponent.mapInstance.getLayer("route");

    if(startRoute) {
      this.mapComponent.mapInstance.removeLayer("startRoute");
      this.mapComponent.mapInstance.removeSource("startRoute");
    }
    if(endRoute) {
      this.mapComponent.mapInstance.removeLayer("endRoute");
      this.mapComponent.mapInstance.removeSource("endRoute");
    }
    if(route) {
      this.mapComponent.mapInstance.removeLayer("route");
      this.mapComponent.mapInstance.removeSource("route");
    }

    if(this.startMarker) {
      this.startMarker.remove();
    }
  }

  public share(poi: CustomMapGeoJSONFeature) {
    // This could be the deeplink base url in the future so that the PWA can be shown if the App is not installed on the device
    // const baseURL = `https://app.itmc.tu-dortmund.de/#`;
    const baseURL = `https://tu-dortmund.de/app`;
    const segments = this.activatedRoute.snapshot.pathFromRoot.flatMap(route => route.url).map(segment => segment.path);

    Share.share({
      title: poi.description?.['de'],
      text: `${baseURL}/${segments[0]}/${poi?.category?.id}/${poi?.id}`
    });
  }

  public getMetaRoom(roomId: number): AppointmentRoom {
    if(this.appointmentRooms === null) return null;
    const index = this.appointmentRooms.findIndex((metaRoom) => metaRoom.room.id === roomId);
    return this.appointmentRooms[index];
  }

  private geoJSONFeatureFromPath(path: number[][]): GeoJSON.Feature {
    return {
      type: 'Feature',
          properties: {},
          geometry: {
              type: 'LineString',
              coordinates: path
          }
    };
  }
}
